import axios from 'axios';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

/*
 * Development only: send `noCache=1` with every request while the flag is set.
 *
 * Timing a chart means timing a cache miss, and the alternatives are both bad. Changing a date
 * range forces one but asks a different question, so the numbers are not the ones under
 * investigation. Calling the API by hand means reproducing the auth header and a dozen query
 * parameters exactly, and getting one wrong measures a different query without saying so.
 *
 * The flag lives in localStorage rather than in the source so measuring does not need a rebuild
 * between runs, and it is read per request so it can be turned on and off mid-session. The
 * server ignores the parameter entirely in production; this guard means it is never sent there
 * either.
 */
const NO_CACHE_KEY = 'mobeat:noCache';

if (process.env.NODE_ENV !== 'production') {
  axiosInstance.interceptors.request.use((config) => {
    if (localStorage.getItem(NO_CACHE_KEY) !== '1') return config;

    return { ...config, params: { ...config.params, noCache: 1 } };
  });

  // Reachable from the console so a measurement run is one command, not an edit.
  window.mobeatNoCache = (on = true) => {
    if (on) localStorage.setItem(NO_CACHE_KEY, '1');
    else localStorage.removeItem(NO_CACHE_KEY);
    return `chart cache bypass ${on ? 'on' : 'off'}`;
  };
}

axiosInstance.interceptors.response.use(
  (response) => {
    if (response.data.success === false) {
      return Promise.reject(response.data.message);
    } else if (response.data.success === true) {
      return response.data.data;
    } else {
      throw new Error('Something went wrong');
    }
  },
  (error) => {
    const data = error.response?.data;
    if (typeof data === 'string') {
      return Promise.reject(data);
    }
    if (data?.message) {
      return Promise.reject(data.message);
    }
    if (data?.error) {
      return Promise.reject(data.error);
    }
    return Promise.reject('Something went wrong');
  },
);

export default axiosInstance;
